import { Entry, EntryFields, RichTextContent } from "contentful"
import { JSONSchema7 } from "json-schema"
import { FieldSet } from "universal-schema/lib"
import {
    APIAppTemplateModel,
    Protocol,
    TemplateName
} from "../API/AppTemplateModel"
import { DataProgramExample } from "../Client/Marketplace"
import { jsonSchemaV004 } from "universal-schema/lib/0.0.4/schema_json"

export enum ContentType {
    category = "title",
    dataProgram = "dataProgram",
    tracking = "tracking"
}

export interface QuestionAndAnswer {
    question: EntryFields.Text
    answer: EntryFields.RichText
}

export interface Schema {
    type: EntryFields.Text
    name: EntryFields.Text
}

export interface DataProgramModel {
    slug: EntryFields.Text
    title: EntryFields.Text
    summary: EntryFields.Text
    description: EntryFields.RichText
    examples?: (Entry<DataProgramExample> | UnresolvedLink)[]
    inputSchema: Entry<Schema>[]
    outputSchema: Entry<Schema>[]
    parametersSchema: Entry<Schema>[]
    questionsAndAnswers: Entry<QuestionAndAnswer>[]
    tags?: EntryFields.Text[]
}

export interface TrackingModel {
    title: EntryFields.Text
    track: EntryFields.Object
}

export interface UnresolvedLink {
    sys: {
        type: "Link"
    }
}

export function isUnresolvedLink(value: {
    sys: { type: string }
}): value is UnresolvedLink {
    return value.sys.type === "Link"
}

function toUniversalSchema(schemas: Entry<Schema>[]): JSONSchema7 {
    const obj = schemas.reduce(
        (passed, f) => ({
            ...passed,
            [f.fields.name]: {
                $ref: f.fields.type
            }
        }),
        {}
    )

    return {
        $schema: jsonSchemaV004.$id,
        type: "object",
        properties: obj,
        required: Object.keys(obj)
    }
}

function toFieldSet(schemas: Entry<Schema>[]): FieldSet {
    return {
        properties: schemas.reduce(
            (passed, f) => ({
                ...passed,
                [f.fields.name]: {
                    title: f.fields.name,
                    example: [],
                    description: f.fields.name
                }
            }),
            {}
        )
    }
}

function richTextContentToString(content: RichTextContent): string {
    return typeof content.value === "string"
        ? content.value
        : content.content === undefined
        ? ""
        : content.content.map(richTextContentToString).join("")
}

export function toAPIAppTemplateModel(
    e: Entry<DataProgramModel>
): APIAppTemplateModel {
    const description = e.fields.description.content
        .map(richTextContentToString)
        .join("")

    return {
        name: TemplateName(e.fields.slug), // for SEO templates, slug === name
        appParamsSchema: null,
        defaultAppParams: null,
        defaultAppMetrics: { metric: {} },
        defaultConstraints: {},
        inputSchema: toUniversalSchema(e.fields.inputSchema),
        outputSchema: toUniversalSchema(e.fields.outputSchema),
        metadata: {
            name: e.fields.title,
            summary: e.fields.summary,
            author: "super.AI",
            tags: e.fields.tags,
            description,
            parameter_description: {
                parameters: toFieldSet(e.fields.parametersSchema),
                inputs: toFieldSet(e.fields.inputSchema),
                outputs: toFieldSet(e.fields.outputSchema)
            }
        },
        uuid: "",
        created: "",
        measurement_job_cost: 0,
        protocol: Protocol.WEBSOCKET,
        visibility: "PUBLIC"
    }
}
